/**
 * Overlay Services Store - Pinia
 * 
 * Manages overlay layer providers (weather, traffic, etc.).
 * Migrated from VueX module src/store/modules/overlayServices/
 */

import { defineStore } from 'pinia';
import { appDebugLog } from '@/utils/appDebugLog';
import GeoMoverDB from '@/db/geoMoverDB';

export const useOverlayServicesStore = defineStore('overlayServices', {
  state: () => ({
    overlayServices: [],
    activeOverlayLayers: [],
    layerOpacities: {},
    overlayLayersCreated: false,
    needsReinitialize: false
  }),

  getters: {
    getAllOverlayServices: (state) => state.overlayServices,
    getActiveOverlayLayers: (state) => state.activeOverlayLayers,
    getLayerOpacities: (state) => state.layerOpacities,
    getLayerOpacity: (state) => (layerId) => state.layerOpacities[layerId] || 0.7,
    getOverlayLayersCreated: (state) => state.overlayLayersCreated,
    getNeedsReinitialize: (state) => state.needsReinitialize,
    isOverlayLayerActive: (state) => (layerId) => state.activeOverlayLayers.includes(layerId)
  },

  actions: {
    /**
     * Fetch all overlay services from IndexedDB
     */
    async fetchAllOverlayServices () {
      try {
        const allOverlayServices = await GeoMoverDB.findAll('overlayServices', null, 'asc');
        this.overlayServices = allOverlayServices;
        return allOverlayServices;
      }
      catch (error) {
        appDebugLog.error('OverlayServices', 'Failed to fetch overlay services:', error);
        this.overlayServices = [];
        return [];
      }
    },

    /**
     * Load persisted overlay state from IndexedDB
     */
    async loadOverlayState () {
      try {
        const state = await GeoMoverDB.findOne('overlayState', 1);
        if (state) {
          this.activeOverlayLayers = state.activeOverlayLayers || [];
          this.layerOpacities = state.layerOpacities || {};
        }
      }
      catch (error) {
        appDebugLog.warn('Could not load overlay state:', error);
      }
    },

    /**
     * Save current overlay state to IndexedDB
     */
    async saveOverlayState () {
      try {
        await GeoMoverDB.updateOne('overlayState', {
          activeOverlayLayers: [...this.activeOverlayLayers],
          layerOpacities: { ...this.layerOpacities },
          lastUpdated: Date.now()
        }, 1);
      }
      catch (error) {
        appDebugLog.warn('Could not save overlay state:', error);
      }
    },

    /**
     * Toggle an overlay layer on/off
     */
    async toggleOverlayLayer (layerId) {
      const index = this.activeOverlayLayers.indexOf(layerId);
      if (index > -1) {
        this.activeOverlayLayers.splice(index, 1);
      }
      else {
        this.activeOverlayLayers.push(layerId);
      }
      await this.saveOverlayState();
    },

    /**
     * Set opacity for a specific overlay layer
     */
    async setLayerOpacity ({ layerId, opacity }) {
      this.layerOpacities = {
        ...this.layerOpacities,
        [layerId]: opacity
      };
      await this.saveOverlayState();
    },

    /**
     * Update an overlay service in IndexedDB
     */
    async updateOverlayService ({ id, data }) {
      await GeoMoverDB.updateOne('overlayServices', data, id);
      await this.fetchAllOverlayServices();
    },

    setOverlayLayersCreated (bool) {
      this.overlayLayersCreated = bool;
    },

    requestReinitialize () {
      this.needsReinitialize = true;
    },

    clearReinitialize () {
      this.needsReinitialize = false;
    },

    toggleOverlayServiceActive (id) {
      const service = this.overlayServices.find(s => s.id === id);
      if (service) {
        service.active = !service.active;
      }
    },

    setAllOverlayServicesActive (active) {
      this.overlayServices.forEach(service => {
        service.active = active;
      });
    }
  }
});
